import {
  APIError,
  customFetch,
  renameFilterProps,
  transformParamsToString,
  ErrorMessage,
  FetchParams,
  DEFAULT_API_URL,
  mergeTraceHeaders,
} from '../../common';

export type GetThreadsParams = Thread.RequestFilters & {
  fields?: string[];
  /** Maximum number of threads to return. Use count: 1 for collection lookups (e.g. page thread, CMS collections, dynamic local nav). */
  count?: number;
} & FetchParams & {
    /** When provided, fetches a single thread by ID (preview mode). Requires token for auth. */
    threadId?: string;
    /** Bearer token for preview mode. Required when threadId is provided. */
    token?: string;
  };

export const getThreads = async ({
  fetchFn,
  params,
}: {
  fetchFn: typeof fetch;
  params: GetThreadsParams;
}): Promise<Thread.Response | Thread.ThreadObject> => {
  const {
    callerId,
    sort,
    count,
    anchor,
    layout,
    fields,
    threadId,
    token,
    fetchParams: { timeout, baseUrl = DEFAULT_API_URL } = {},
    resource,
    traceHeaders,
    ...filters
  } = params;

  if (token && !threadId?.trim()) {
    throw new APIError(ErrorMessage.GENERIC, {
      originalMessage:
        'Preview mode requires threadId when token is provided. Cannot fall back to list mode.',
      url: `${DEFAULT_API_URL}/product_feed/threads/v2`,
    });
  }

  if (threadId?.trim() && !token) {
    throw new APIError(ErrorMessage.GENERIC, {
      originalMessage:
        'Preview mode requires token when threadId is provided. Authorization header is required.',
      url: `${DEFAULT_API_URL}/product_feed/threads/v2`,
    });
  }

  const isPreviewById = Boolean(threadId?.trim());

  const paramsString = isPreviewById
    ? transformParamsToString({
        layout,
        preview: true,
        ...filters,
      })
    : transformParamsToString({
        anchor,
        count,
        fields,
        filter: renameFilterProps(filters as Thread.RequestFilters),
        layout,
        sort,
      });

  const path = 'product_feed/threads/v2';
  const url = isPreviewById
    ? `${baseUrl}/${path}/${threadId}/${paramsString}`
    : `${baseUrl}/${path}/${paramsString}`;

  const baseHeaders: Record<string, string> = callerId
    ? { 'nike-api-caller-id': callerId }
    : {};
  if (token) {
    baseHeaders.Authorization = `Bearer ${token}`;
  }

  const requestOptions = {
    headers: mergeTraceHeaders(baseHeaders, traceHeaders),
    method: 'GET',
    path,
    timeout,
    vipAddress: 'product_feed-threads-v2',
  };

  const result = await customFetch({
    fetchFn,
    options: requestOptions,
    resource,
    url,
  });

  try {
    return await result.json();
  } catch (e) {
    throw new APIError(ErrorMessage.JSON_PARSE, {
      originalMessage: e.message,
      requestOptions,
      url,
    });
  }
};
